import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-black bg-white hover:text-primary/60 border border-gray-200",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        black: "bg-black text-white hover:bg-opacity-80 border border-gray-200",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        outline2:
          "border border-input bg-transparent text-white hover:bg-accent hover:text-accent-foreground",
        outline3:
          "border border-black bg-transparent text-black hover:bg-white hover:bg-opacity-30 hover:text-accent-foreground",
        gray: "border border-input bg-gray-800 hover:bg-gray-600 text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        takyon: "bg-takyon text-takyon-foreground hover:bg-takyon/70",
        doubleBorder:
          "flex items-center gap-2 rounded-full px-6 py-2 bg-white hover:bg-gray-200",
        gradient: "bg-gradient text-black hover:opacity-90 transition-opacity",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return variant === "doubleBorder" ? (
      <div className="p-1 bg-gradient rounded-full">
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
      </div>
    ) : (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
